import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Undo2 } from 'lucide-react';
import { FormSheet } from '@/components/common/FormSheet';
import { FormField } from '@/components/common/FormField';
import { DialogEyebrow } from '@/components/common/DialogPrimitives';
import { DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ordersService } from '@/services/orders';
import {
  returnsService,
  type CreateReturnLinePayload,
  type CreateReturnPayload,
} from '@/services/returns';
import { fmt } from '@/lib/format';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Pre-fill order picker when opened from an order detail. */
  prefillOrderId?: string;
}

interface DraftReturnLine {
  salesOrderItemId: string;
  /** Line snapshot for display. */
  sku: string;
  name: string;
  shipped: number;
  /** Operator-entered return quantity (capped at shipped). */
  quantity: string;
  reason: string;
}

const REASON_OPTIONS = [
  'Damaged in transit',
  'Defective on arrival',
  'Wrong item shipped',
  'Wrong quantity',
  'Customer changed mind',
  'Late delivery refused',
  'Other',
];

/**
 * Operator-facing "log a return" form. Picks an existing sales order →
 * pulls its line items → operator enters per-line return quantity (capped
 * at shipped) + a reason. Closes the long-standing gap where the BE
 * exposed `POST /returns` but the FE had no surface for it.
 *
 * Filters the order picker to dispatched / delivered orders since you
 * can only return what physically shipped.
 */
export function ReturnFormSheet({ open, onOpenChange, prefillOrderId }: Props) {
  const qc = useQueryClient();

  const [orderId, setOrderId] = useState<string>('');
  const [externalRef, setExternalRef] = useState('');
  const [reason, setReason] = useState<string>('');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<DraftReturnLine[]>([]);

  // Order picker — only show shipped/delivered orders.
  const ordersQuery = useQuery({
    queryKey: ['orders', { forReturn: true }],
    queryFn: () => ordersService.list({ pageSize: 200 }),
    enabled: open,
    staleTime: 30_000,
  });
  const eligibleOrders = useMemo(
    () =>
      (ordersQuery.data?.rows ?? []).filter((o) =>
        ['DISPATCHED', 'DELIVERED'].includes(o.status),
      ),
    [ordersQuery.data],
  );

  // Detail fetch for the picked order so we have its line items.
  const orderDetailQuery = useQuery({
    queryKey: ['orders', 'detail', orderId],
    queryFn: () => ordersService.get(orderId),
    enabled: !!orderId && open,
  });
  const order = orderDetailQuery.data;

  // Hydrate / re-hydrate draft lines when the picked order changes.
  useEffect(() => {
    if (!order) {
      setLines([]);
      return;
    }
    setLines(
      order.items.map((it) => ({
        salesOrderItemId: it.id,
        sku: it.sku,
        name: it.name,
        shipped: Number(it.quantity),
        quantity: '',
        reason: '',
      })),
    );
  }, [order]);

  // Reset on open / prefill.
  useEffect(() => {
    if (!open) return;
    setOrderId(prefillOrderId ?? '');
    setExternalRef('');
    setReason('');
    setNotes('');
    setLines([]);
  }, [open, prefillOrderId]);

  const updateLine = (id: string, patch: Partial<DraftReturnLine>) =>
    setLines((ls) => ls.map((l) => (l.salesOrderItemId === id ? { ...l, ...patch } : l)));

  const built: CreateReturnLinePayload[] = lines
    .map((l) => ({
      salesOrderItemId: l.salesOrderItemId,
      quantity: Number(l.quantity),
      reason: l.reason.trim() || undefined,
    }))
    .filter((l) => Number.isFinite(l.quantity) && l.quantity > 0);

  const overdraw = lines.some((l) => Number(l.quantity) > l.shipped);
  const valid = !!orderId && built.length > 0 && !overdraw;

  const mutation = useMutation({
    mutationFn: () => {
      const payload: CreateReturnPayload = {
        salesOrderId: orderId,
        items: built,
        externalReturnRef: externalRef.trim() || undefined,
        reason: reason.trim() || undefined,
        notes: notes.trim() || undefined,
      };
      return returnsService.create(payload);
    },
    onSuccess: (returnOrder) => {
      toast.success(`Return ${returnOrder.returnNumber} created`);
      qc.invalidateQueries({ queryKey: ['returns'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      onOpenChange(false);
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : 'Could not create return'),
  });

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!valid) return;
    mutation.mutate();
  };

  return (
    <FormSheet
      open={open}
      onOpenChange={onOpenChange}
      headerSlot={
        <div className="flex items-start gap-4">
          <span
            aria-hidden="true"
            className="grid h-14 w-14 shrink-0 place-items-center rounded-lg bg-gradient-to-br from-warning/10 to-warning/5 text-[hsl(38_85%_30%)] ring-1 ring-inset ring-warning/15"
          >
            <Undo2 className="h-6 w-6" />
          </span>
          <div className="min-w-0 flex-1 space-y-1.5">
            <DialogEyebrow>New return</DialogEyebrow>
            <DialogTitle className="text-[18px] font-semibold leading-tight tracking-tight">
              Log a return against an order
            </DialogTitle>
            {order ? (
              <div className="flex flex-wrap items-center gap-1.5 pt-0.5">
                <Badge variant="muted" className="font-mono text-[10px]">
                  {order.orderNumber}
                </Badge>
                <Badge variant="muted" className="text-[10px]">
                  {order.customer.name}
                </Badge>
                <Badge variant="muted" className="font-mono text-[10px]">
                  {order.warehouse.code}
                </Badge>
              </div>
            ) : (
              <DialogDescription className="text-[12.5px] leading-relaxed">
                Pick a dispatched order, choose which lines came back and how many units.
                The return opens at REQUESTED — operators move it through APPROVED → RECEIVED →
                QC from the detail sheet.
              </DialogDescription>
            )}
          </div>
        </div>
      }
      title=""
      submitLabel="Create return"
      onSubmit={onSubmit}
      isSubmitting={mutation.isPending}
      error={mutation.error}
      size="5xl"
      fixedHeight="lg"
    >
      <FormField label="Sales order" htmlFor="orderId" required>
        <Select value={orderId} onValueChange={setOrderId}>
          <SelectTrigger id="orderId" className="h-9">
            <SelectValue
              placeholder={
                ordersQuery.isLoading ? 'Loading…' : 'Select a dispatched / delivered order'
              }
            />
          </SelectTrigger>
          <SelectContent>
            {eligibleOrders.length === 0 ? (
              <SelectItem value="__none" disabled>
                No dispatched orders to return against
              </SelectItem>
            ) : (
              eligibleOrders.map((o) => (
                <SelectItem key={o.id} value={o.id}>
                  <span className="font-mono">{o.orderNumber}</span>
                  <span className="ml-2 text-muted-foreground">
                    {o.customer.name} ·{' '}
                    {o.grandTotal != null
                      ? fmt.inr(Number(o.grandTotal))
                      : o.totalAmount != null
                        ? fmt.inr(Number(o.totalAmount))
                        : '—'}
                  </span>
                </SelectItem>
              ))
            )}
          </SelectContent>
        </Select>
      </FormField>

      <div className="grid grid-cols-2 gap-4">
        <FormField
          label="Reason"
          htmlFor="reason"
          hint="Drop-down convenience; the BE stores this verbatim."
        >
          <Select value={reason} onValueChange={setReason}>
            <SelectTrigger id="reason" className="h-9">
              <SelectValue placeholder="Pick a reason" />
            </SelectTrigger>
            <SelectContent>
              {REASON_OPTIONS.map((r) => (
                <SelectItem key={r} value={r}>
                  {r}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormField>
        <FormField
          label="External return ref"
          htmlFor="externalRef"
          hint="Optional — your CRM / portal's return ID."
        >
          <Input
            id="externalRef"
            value={externalRef}
            onChange={(e) => setExternalRef(e.target.value)}
            placeholder="RTN-2026-0042"
            className="h-9"
          />
        </FormField>
      </div>

      {orderDetailQuery.isLoading && orderId && (
        <div className="rounded-md border border-border/70 bg-muted/30 px-3 py-3 text-[12.5px] text-muted-foreground">
          Loading order line items…
        </div>
      )}

      {order && order.items.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-baseline justify-between">
            <h3 className="text-[12.5px] font-semibold tracking-tight text-foreground">
              Return lines
            </h3>
            <span className="text-[11px] text-muted-foreground">
              {built.length} of {order.items.length} line{order.items.length === 1 ? '' : 's'} selected
            </span>
          </div>
          <div className="overflow-hidden rounded-lg border border-border/70">
            <div className="grid grid-cols-12 gap-3 border-b border-border/60 bg-muted/40 px-4 py-2 text-[10px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
              <div className="col-span-5">Item</div>
              <div className="col-span-2 text-right">Shipped</div>
              <div className="col-span-2 text-right">Returning</div>
              <div className="col-span-3">Reason override</div>
            </div>
            {lines.map((line) => {
              const qty = Number(line.quantity);
              const over = Number.isFinite(qty) && qty > line.shipped;
              return (
                <div
                  key={line.salesOrderItemId}
                  className="grid grid-cols-12 items-center gap-3 border-b border-border/60 px-4 py-2.5 text-[12.5px] last:border-0"
                >
                  <div className="col-span-5 min-w-0">
                    <div className="truncate font-medium text-foreground">{line.name}</div>
                    <div className="font-mono text-[10.5px] text-muted-foreground">{line.sku}</div>
                  </div>
                  <div className="col-span-2 text-right font-mono tabular-nums text-muted-foreground">
                    {fmt.number(line.shipped)}
                  </div>
                  <div className="col-span-2">
                    <Input
                      type="number"
                      inputMode="decimal"
                      min={0}
                      max={line.shipped}
                      step="0.001"
                      value={line.quantity}
                      onChange={(e) =>
                        updateLine(line.salesOrderItemId, { quantity: e.target.value })
                      }
                      placeholder="0"
                      className={
                        over
                          ? 'h-7 border-destructive/40 text-right font-mono text-[12px] tabular-nums'
                          : 'h-7 text-right font-mono text-[12px] tabular-nums'
                      }
                    />
                  </div>
                  <div className="col-span-3">
                    <Input
                      value={line.reason}
                      onChange={(e) =>
                        updateLine(line.salesOrderItemId, { reason: e.target.value })
                      }
                      placeholder={reason || 'Same as header'}
                      className="h-7 text-[12px]"
                    />
                  </div>
                </div>
              );
            })}
          </div>
          {overdraw && (
            <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-[12px] text-destructive">
              At least one line's return quantity exceeds the shipped quantity. Cap each line at what was actually delivered.
            </div>
          )}
        </div>
      )}

      <FormField
        label="Notes"
        htmlFor="notes"
        hint="Optional — appears on the return record + audit trail."
      >
        <Textarea
          id="notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Customer says box arrived crushed · Pickup scheduled for tomorrow"
          rows={2}
        />
      </FormField>

      {!valid && orderId && order && (
        <div className="rounded-md border border-warning/30 bg-warning/5 px-3 py-2 text-[11.5px] text-[hsl(38_85%_30%)]">
          Enter a return quantity &gt; 0 on at least one line.
        </div>
      )}
    </FormSheet>
  );
}

